import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { api, API } from "@/api";
import { inputCls } from "@/admin/ResourceTab";

const STATUSES = ["New", "Under Review", "Shortlisted", "Interview", "Selected", "Rejected", "On Hold"];

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  "Under Review": "bg-amber-50 text-amber-700 border-amber-200",
  Shortlisted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Interview: "bg-violet-50 text-violet-700 border-violet-200",
  Selected: "bg-green-50 text-green-700 border-green-200",
  Rejected: "bg-red-50 text-red-600 border-red-200",
  "On Hold": "bg-slate-100 text-slate-600 border-slate-200",
};

function ApplicationRow({ a, i, onStatus, onDelete }) {
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-ivory/60" data-testid={`application-row-${i}`}>
      <td className="px-5 py-3">
        <p className="font-semibold text-navy-900">{a.fullName}</p>
        <p className="text-xs text-slate-500">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
      </td>
      <td className="px-5 py-3 text-slate-700">{a.position || "—"}</td>
      <td className="px-5 py-3 text-slate-700">{a.brand || "—"}</td>
      <td className="px-5 py-3 text-slate-700">{a.experience || "—"}</td>
      <td className="px-5 py-3 text-xs text-slate-500">{new Date(a.createdAt).toLocaleDateString("en-IN")}</td>
      <td className="px-5 py-3">
        {a.resumePath ? (
          <a
            href={`${API}/applications/${a.id}/resume`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-dark hover:text-navy-900"
            data-testid={`application-resume-${i}`}
          >
            <Download className="h-3.5 w-3.5" /> {a.resumeName || "CV"}
          </a>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-5 py-3">
        <select
          value={a.status}
          onChange={(e) => onStatus(a.id, e.target.value)}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_COLORS[a.status] || ""}`}
          data-testid={`application-status-${i}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-5 py-3">
        <button onClick={() => onDelete(a.id)} aria-label="Delete application" className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:border-red-400 hover:text-red-500" data-testid={`application-delete-${i}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

export function ApplicationsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (search) params.set("search", search);
    api.get(`/applications?${params.toString()}`).then((r) => setItems(r.data)).catch(() => toast.error("Failed to load applications"));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAppStatus = async (id, s) => {
    try {
      await api.patch(`/applications/${id}`, { status: s });
      toast.success(`Status updated to ${s}`);
      load();
    } catch {
      toast.error("Status update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/applications/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-navy-900">Job Applications</h2>
        <div className="flex gap-3">
          <select className={`${inputCls} !w-44`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="applications-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            placeholder="Search name / email / role…"
            className={`${inputCls} !w-56`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="applications-search"
          />
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="applications-table">
        {items.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-slate-500">No applications received yet.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-ivory">
                {["Candidate", "Role", "Brand", "Experience", "Applied", "Resume", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a, i) => (
                <ApplicationRow key={a.id} a={a} i={i} onStatus={setAppStatus} onDelete={remove} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function MessagesTab({ resource, title, testid }) {
  const [items, setItems] = useState([]);

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data)).catch(() => toast.error(`Failed to load ${title}`));
  useEffect(() => { load(); }, [resource]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/${resource}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`admin-${testid}`}>
      <h2 className="font-serif text-2xl text-navy-900">{title}</h2>
      <div className="mt-6 space-y-4" data-testid={`${testid}-list`}>
        {items.length === 0 ? (
          <p className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500">
            No {title.toLowerCase()} received yet.
          </p>
        ) : (
          items.map((m, i) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`${testid}-item-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-navy-900">{m.name}{m.organisation ? ` · ${m.organisation}` : ""}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {m.email}{m.phone ? ` · ${m.phone}` : ""} · {new Date(m.createdAt).toLocaleString("en-IN")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full bg-navy-950 px-3 py-0.5 text-[11px] font-semibold text-gold">{m.category}</span>
                    {m.subject && <span className="rounded-full border border-slate-200 px-3 py-0.5 text-[11px] text-slate-600">{m.subject}</span>}
                  </div>
                  <p className="mt-3 max-w-3xl whitespace-pre-line text-sm leading-relaxed text-slate-600">{m.message}</p>
                </div>
                <button onClick={() => remove(m.id)} aria-label="Delete" className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:border-red-400 hover:text-red-500" data-testid={`${testid}-delete-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
